import {
  BadgeCheckSolidIcon,
  DownLongRegularIcon,
  PaperPlaneRegularIcon,
  PrintRegularIcon,
  WhatsAppBrandIcon,
  FileRegularIcon,
  UploadRegularIcon,
  CheckRegularIcon,
} from "@elixir/icons";

import { useReceiptStore } from "@elixir/stores";
import { Modal, notification } from "antd";
import {
  PdfRenderer,
  PrimaryButton,
  ReceiptActionItem,
  SecondaryButton,
  Spinner,
} from "ui";
import {
  closeReceiptPopupEvent,
  createNewReceipt,
  editReceipt,
} from "../../utils/receipt";
import { sendBusinessReceiptWAMessage } from "apis";
import { ERROR_CODE, RECEIPT_FLOW_STATES, RECEIPT_TYPE, SUBJECT_KEYS } from "@elixir/types";
import { PROGRESS_STEPS } from "@elixir/stores/src/receipt-store";
import { useEffect, useRef, useState } from "react";
import {
  generateReceiptPDFOnClient,
  PAGIFY_FAILURE_FALLBACK_STRING,
} from "../../receiptOnClient/GeneratePDFOnClient";
import uploadReceiptBlobV2 from "../../apis/parchi/put-receipt-blob-v2";
import { serializeErrorForTracking, track } from "../../utils/tracking";
import useCreateReceiptPdfV2 from "../../apis/tryst/create-receipt-pdf-v2-handler";
import { getAclViewClassName } from "@elixir/utils";

const ExclamationTriangleRegularIcon = ({ className }) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 640"
    >
      <path d="M320 64C334.7 64 348.2 72.1 355.2 85L571.2 485C577.9 497.4 577.6 512.4 570.4 524.5C563.2 536.6 550.1 544 536 544L104 544C89.9 544 76.8 536.6 69.6 524.5C62.4 512.4 62.1 497.4 68.8 485L284.8 85C291.8 72.1 305.3 64 320 64zM320 416C302.3 416 288 430.3 288 448C288 465.7 302.3 480 320 480C337.7 480 352 465.7 352 448C352 430.3 337.7 416 320 416zM320 224C301.8 224 287.3 239.5 288.6 257.7L296 361.7C296.9 374.2 307.4 384 319.9 384C332.5 384 342.9 374.3 343.8 361.7L351.2 257.7C352.5 239.5 338.1 224 319.8 224z" />
    </svg>
  );
};
const ERROR_STATES = {
  PDF_GENERATION_FAILED: "PDF_GENERATION_FAILED",
  S3_UPLOAD_FAILED: "S3_UPLOAD_FAILED",
  GENERAL_ERROR: "GENERAL_ERROR",
};

export default function UIForReceiptMadeonLocal() {
  const receipt = useReceiptStore((state) => state.receipt);
  const receiptState = useReceiptStore((state) => state.receiptState);
  const closeReceiptPopup = useReceiptStore(
    (state) => state.setCloseReceiptPopup
  );
  const selectedAppointment = useReceiptStore(
    (state) => state.selectedAppointment
  );

  const setSelectedAppointment = useReceiptStore(
    (state) => state.setSelectedAppointment
  );

  const { mutateAsync: createReceiptPdfServer } = useCreateReceiptPdfV2();

  const [blobUrlOfPdf, setBlobUrlOfPdf] = useState<string | undefined>(
    undefined
  );
  const [errorState, setErrorState] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const setReceipt = useReceiptStore((state) => state.setReceipt);
  const fileNameReceiptV2 = useReceiptStore((state) => state.receiptFileNameV2);
  const { setCurrentStep, setIsProcessing } = useReceiptStore.getState();
  const {
    currentStep,
    isProcessing,
    patientData,
    doctorProfile,
    pdfConfigForGeneration,
    receiptData,
  } = useReceiptStore((state) => ({
    currentStep: state.currentStep,
    isProcessing: state.isProcessing,
    patientData: state.patientData,
    doctorProfile: state.doctorProfile,
    pdfConfigForGeneration: state.pdfConfigForGeneration,
    receiptData: state.receiptData,
  }));

  const onReceiptSuccess = useReceiptStore((state) => state.onReceiptSuccess);

  const generatePDF = async () => {
    try {
      setCurrentStep(PROGRESS_STEPS.GENERATING_PDF);

      const resOfPdf = await generateReceiptPDFOnClient({
        receiptData,
        pdfConfigForGeneration,
        patientData: patientData!,
        doctorProfile: doctorProfile!,
      });

      if (resOfPdf.success) {
        setCurrentStep(PROGRESS_STEPS.PDF_SUCCESS);
        setBlobUrlOfPdf(resOfPdf.pdfBlobUrl as string);
        setPdfBlob(resOfPdf.blob as Blob);
        return resOfPdf.blob;
      } else {
        throw new Error("PDF generation failed");
      }
    } catch (err: any) {
      console.error("Error in PDF generation:", err);
      track(`PDF_GENERATION_FAILED`, {
        on: String(receipt?.receipt_id),
        what: serializeErrorForTracking(err),
        stack: err?.stack,
      });
      if (!err?.message?.includes(PAGIFY_FAILURE_FALLBACK_STRING)) {
        setErrorState(ERROR_STATES.PDF_GENERATION_FAILED);
        setCurrentStep(PROGRESS_STEPS.ERROR);
        setIsProcessing(false);
      }
      throw err;
    }
  };

  const uploadToS3 = async (blob: Blob) => {
    if (!fileNameReceiptV2) {
      alert("could not get receipt file name");
      return;
    }
    try {
      setCurrentStep(PROGRESS_STEPS.SAVING_RESPONSE);
      setErrorState(null);
      const uploadToS3Res = await uploadReceiptBlobV2({
        file: blob,
        fileName: fileNameReceiptV2,
      });

      if (uploadToS3Res?.success) {
        setCurrentStep(PROGRESS_STEPS.COMPLETED);
        setReceipt({
          ...(receipt ?? {}),
          receipt_url: uploadToS3Res?.url,
        });
        setIsProcessing(false);
        return uploadToS3Res;
      } else {
        throw new Error("S3 upload failed");
      }
    } catch (err: any) {
      console.error("Error in S3 upload:", err);
      track(`S3_UPLOAD_FAILED`, {
        on: String(receipt?.receipt_id),
        what: serializeErrorForTracking(err),
        stack: err?.stack,
        fileName: fileNameReceiptV2,
      });
      setErrorState(ERROR_STATES.S3_UPLOAD_FAILED);
      setCurrentStep(PROGRESS_STEPS.ERROR);
      setIsProcessing(false);
      throw err;
    }
  };

  const processReceipt = async () => {
    setIsProcessing(true);
    const blob = await generatePDF();
    await uploadToS3(blob as Blob);
    onReceiptSuccess?.();
  };

  useEffect(() => {
    if (
      patientData &&
      doctorProfile &&
      receipt &&
      currentStep === PROGRESS_STEPS.CREATING_RECEIPT
    ) {
      processReceipt().catch(async (err) => {
        console.error("Error in main receipt processing:", err);
        track(`MAIN_RECEIPT_FLOW_BROKE`, {
          on: String(receipt?.receipt_id),
          what: serializeErrorForTracking(err),
          stack: err?.stack,
        });

        if (err?.message?.includes(PAGIFY_FAILURE_FALLBACK_STRING)) {
          track("ERROR", {
            receipt_id: receipt.receipt_id?.toString(),
            identifier: "PAGIFY_ERROR_DETECTED_RETRYING_SERVER",
          });

          try {
            useReceiptStore.getState().setforceServerReceiptGeneration(true);
            const pdfObject = (err as any)?.pdfObject;

            if (!pdfObject) {
              throw new Error("Missing pdfObject for server generation");
            }

            const response = await createReceiptPdfServer({ pdfObject });

            if (!response?.receipt_url) {
              throw new Error("PDF generation failed");
            }

            setReceipt({
              ...receipt,
              receipt_url: response.receipt_url,
            });

            if (selectedAppointment) {
              setSelectedAppointment({
                ...selectedAppointment,
                receipts: selectedAppointment.receipts.map((r) => {
                  if (r.receipt_id == receipt.receipt_id) {
                    console.log("mathc hit", receipt);

                    return {
                      ...r,
                      receipt_url: response?.receipt_url as string,
                    };
                  }
                  return r;
                }),
              });
            }
            onReceiptSuccess?.();

            useReceiptStore.getState().setforceServerReceiptGeneration(false);
            track("SUCCESS", {
              receipt_id: receipt.receipt_id?.toString(),
              identifier: "SERVER_PDF_GENERATION_SUCCESS",
            });
            setCurrentStep(PROGRESS_STEPS.COMPLETED);
            setIsProcessing(false);
          } catch (serverError) {
            useReceiptStore.getState().setforceServerReceiptGeneration(true);
            console.error(
              "ERROR ======> Server PDF generation failed",
              serverError
            );
            track("ERROR", {
              receipt_id: receipt.receipt_id?.toString(),
              identifier: "SERVER_PDF_GENERATION_FAILED",
            });
            throw serverError;
          }
        } else {
          throw err;
        }
      });
    }
  }, [patientData, doctorProfile, receipt]);

  const retryPDFGeneration = async () => {
    setIsProcessing(true);
    try {
      const blob = await generatePDF();
      await uploadToS3(blob!);
    } catch (err) { }
  };

  const retryS3Upload = async () => {
    if (!pdfBlob) return;
    setIsProcessing(true);
    try {
      await uploadToS3(pdfBlob);
    } catch (err) { }
  };

  const regenerateReceipt = () => {
    if (receipt) {
      setErrorState(null);
      processReceipt();
    }
  };

  const sendViaOwnWhatsApp = () => {
    try {
      const wpString = `Here is the payment receipt for your consultation : ${receipt?.receipt_url}`;
      window.open(
        `https://api.whatsapp.com/send?phone=${receipt?.mobile}&text=${wpString}`,
        "_blank"
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleModalClose = () => {
    if (isProcessing) {
      notification.warning({
        message: "Receipt Still Being Processed",
        description:
          "The receipt is still being processed. Please wait for completion.",
        placement: "bottomRight",
      });
      return;
    }
    closeReceiptPopup();
    closeReceiptPopupEvent();
  };

  const ContactSupportUI = () => {
    return (
      <p className="dr-text-12 dr-text-darwin-neutral-500">
        Contact support if the issue persists
      </p>
    );
  };
  const renderErrorUI = () => {
    switch (errorState) {
      case ERROR_STATES.PDF_GENERATION_FAILED:
        return (
          <div className="dr-flex dr-flex-col dr-items-center dr-justify-center h-full dr-px-16 dr-text-center">
            <div className="dr-w-64 dr-h-64 dr-rounded-full dr-bg-red-50 dr-flex dr-items-center dr-justify-center dr-mb-24">
              <ExclamationTriangleRegularIcon
                className={"dr-w-32 dr-h-32 dr-text-red-500"}
              />
            </div>
            <h3 className="dr-text-18 dr-font-600 dr-text-darwin-neutral-1000 dr-mb-8">
              PDF Generation Failed
            </h3>
            <p className="dr-text-14 dr-text-darwin-neutral-600 dr-mb-24 dr-max-w-320">
              Oops! PDF generation failed. Don't worry, you're data is safe!
            </p>
            <div className="dr-flex dr-flex-col dr-space-y-8">
              <PrimaryButton
                title="Regenerate PDF"
                onClick={retryPDFGeneration}
                isDisabled={isProcessing}
              />
              <ContactSupportUI />
            </div>
          </div>
        );

      case ERROR_STATES.S3_UPLOAD_FAILED:
        return (
          <div className="dr-flex dr-flex-col dr-items-center dr-justify-center h-full dr-px-16 dr-text-center">
            <div className="dr-w-64 dr-h-64 dr-rounded-full dr-bg-orange-50 dr-flex dr-items-center dr-justify-center">
              <ExclamationTriangleRegularIcon className="dr-w-32 dr-h-32 dr-text-orange-500" />
            </div>
            <h3 className="dr-text-18 dr-font-600 dr-text-darwin-neutral-1000 dr-mb-8">
              Error While Uploading PDF
            </h3>
            <p className="dr-text-14 dr-text-darwin-neutral-600 dr-mb-24 dr-max-w-320">
              Failed to save PDF. Don't worry, you're data is safe.
            </p>
            <div className="dr-flex dr-flex-col dr-space-y-8">
              <PrimaryButton
                title="Retry Upload"
                onClick={retryS3Upload}
                isDisabled={isProcessing}
              />
              <ContactSupportUI />
            </div>
          </div>
        );

      case ERROR_STATES.GENERAL_ERROR:
        return (
          <div className="dr-flex dr-flex-col dr-items-center dr-justify-center h-full dr-px-16 dr-text-center">
            <div className="dr-w-64 dr-h-64 dr-rounded-full dr-bg-red-50 dr-flex dr-items-center dr-justify-center dr-mb-24">
              <ExclamationTriangleRegularIcon className="dr-w-32 dr-h-32 dr-text-red-500" />
            </div>
            <h3 className="dr-text-18 dr-font-600 dr-text-darwin-neutral-1000 dr-mb-8">
              Something Went Wrong
            </h3>
            <p className="dr-text-14 dr-text-darwin-neutral-600 dr-mb-24 dr-max-w-320">
              An unexpected error occurred while processing your receipt. Dont
              worry you're data is safe.
            </p>
            <div className="dr-flex dr-flex-col dr-space-y-8">
              <PrimaryButton
                title="Regenerate Receipt"
                onClick={regenerateReceipt}
                isDisabled={isProcessing}
              />
              <ContactSupportUI />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      width={840}
      centered
      open={receiptState === RECEIPT_FLOW_STATES.VIEW_SINGLE_RECEIPT}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
      style={{ borderRadius: 8 }}
      className="modal-rounded-8"
      closeIcon={<></>}
      onCancel={handleModalClose}
      footer={isProcessing || errorState ? null : <ReceiptActionFooter />}
    >
      <div
        style={{ height: "75vh" }}
        className="dr-flex dr-justify-between dr-min-h-400 dr-p-16 dr-space-x-16"
      >
        <div className="dr-flex-1 dr-flex dr-flex-col dr-space-y-16">
          <div className="dr-flex dr-items-center dr-space-x-8">
            <div className="dr-flex dr-items-center dr-justify-center">
              <BadgeCheckSolidIcon className="dr-text-darwin-green-primary dr-w-24 dr-h-24" />
            </div>
            <p className="dr-text-13 dr-font-700 dr-text-darwin-neutral-1000">
              {receipt?.receipt_type === "DUE_PAYMENT"
                ? `Payment Note for ₹ `
                : `Receipt for ₹ `}
              {receipt?.net_amount?.toLocaleString("en-IN") || "0"} created
              successfully
            </p>
          </div>

          {errorState ? (
            renderErrorUI()
          ) : isProcessing ? (
            <ReceiptMakingProgessiveLoader />
          ) : (
            <div className="dr-flex dr-flex-col dr-space-y-2">
              <ReceiptActionItem
                icon={
                  <PaperPlaneRegularIcon className="dr-w-16 dr-h-16 dr-text-darwin-primary-primary" />
                }
                title="Send"
                subtext="Via SMS and Whatsapp"
                onClick={async () => {
                  if (receipt) {
                    const { success, error_meta } =
                      await sendBusinessReceiptWAMessage({
                        receiptId: receipt.receipt_id!,
                      });

                    if (success) {
                      if (
                        error_meta &&
                        error_meta.code === ERROR_CODE.LOW_CREDITS
                      ) {
                        notification.success({
                          message: error_meta.message,
                          placement: "bottomRight",
                        });
                      } else {
                        notification.success({
                          message: "Receipt sent successfully on whatsapp",
                          placement: "bottomRight",
                        });
                      }
                    } else {
                      notification.error({
                        message: "Failed to Send Receipt",
                        placement: "bottomRight",
                      });
                    }
                  }
                }}
              />
              <ReceiptActionItem
                icon={
                  <PrintRegularIcon className="dr-w-16 dr-h-16 dr-text-darwin-primary-primary" />
                }
                title="Print"
                subtext="View and print the receipt"
                onClick={() => {
                  window.open(receipt?.receipt_url || blobUrlOfPdf, "_blank");
                }}
              />
              <ReceiptActionItem
                icon={
                  <DownLongRegularIcon className="dr-w-16 dr-h-16 dr-text-darwin-primary-primary" />
                }
                title="Download"
                subtext="Get a PDF version of the receipt"
                onClick={() => {
                  window.open(receipt?.receipt_url || blobUrlOfPdf, "_blank");
                }}
              />
              <ReceiptActionItem
                icon={
                  <WhatsAppBrandIcon className="dr-w-16 dr-h-16 dr-text-darwin-primary-primary" />
                }
                title="Send on WhatsApp"
                subtext="Send from your own WhatsApp"
                onClick={sendViaOwnWhatsApp}
              />
            </div>
          )}
        </div>
        <div className="dr-flex-1">
          <PdfRenderer
            key={receipt?.receipt_url || blobUrlOfPdf}
            url={receipt?.receipt_url || blobUrlOfPdf || ""}
          />
        </div>
      </div>
    </Modal>
  );
}

function ReceiptMakingProgessiveLoader() {
  const { currentStep } = useReceiptStore((state) => ({
    currentStep: state.currentStep,
  }));

  const getStepStatus = (step: PROGRESS_STEPS) => {
    const steps = [
      PROGRESS_STEPS.CREATING_RECEIPT,
      PROGRESS_STEPS.GENERATING_PDF,
      PROGRESS_STEPS.PDF_SUCCESS,
      PROGRESS_STEPS.SAVING_RESPONSE,
      PROGRESS_STEPS.COMPLETED,
    ];
    const currentIndex = steps.indexOf(currentStep);
    const stepIndex = steps.indexOf(step);

    if (stepIndex < currentIndex) return "completed";
    if (stepIndex === currentIndex) return "active";
    return "pending";
  };

  const progressSteps = [
    {
      key: PROGRESS_STEPS.CREATING_RECEIPT,
      title: "Creating Receipt",
      description: "Fetching receipt data and config",
      icon: FileRegularIcon,
    },
    {
      key: PROGRESS_STEPS.GENERATING_PDF,
      title: "Generating PDF",
      description: "Creating PDF with the data",
      icon: FileRegularIcon,
    },
    {
      key: PROGRESS_STEPS.PDF_SUCCESS,
      title: "Finalizing Receipt",
      description: "Configuring PDF for sending",
      icon: CheckRegularIcon,
    },
    {
      key: PROGRESS_STEPS.SAVING_RESPONSE,
      title: "Saving to Cloud",
      description: "Storing receipt securely",
      icon: UploadRegularIcon,
    },
    {
      key: PROGRESS_STEPS.COMPLETED,
      title: "Receipt Ready",
      description: "Ready to view and send",
      icon: BadgeCheckSolidIcon,
    },
  ];

  return (
    <div className="dr-flex dr-flex-col dr-space-y-24 dr-py-16">
      <div className="dr-space-y-4">
        {progressSteps.map((step, index) => {
          const status = getStepStatus(step.key);
          const IconComponent = step.icon;

          return (
            <div
              key={step.key}
              className="dr-flex dr-items-center dr-space-x-12 dr-py-8 dr-px-12 dr-rounded-6 dr-transition-all dr-duration-300"
              style={{
                backgroundColor:
                  status === "active" ? "#f8fafc" : "transparent",
                borderLeft:
                  status === "active"
                    ? "3px solid #3b82f6"
                    : "3px solid transparent",
              }}
            >
              <div className="dr-flex dr-items-center dr-justify-center dr-mt-1">
                {status === "completed" ? (
                  <div className="dr-w-18 dr-h-18 dr-rounded-full dr-bg-darwin-green-primary dr-flex dr-items-center dr-justify-center">
                    <CheckRegularIcon className="dr-w-10 dr-h-10 text-white" />
                  </div>
                ) : status === "active" ? (
                  <div className="dr-w-18 dr-h-18 dr-rounded-full dr-flex dr-items-center dr-justify-center">
                    <Spinner className="dr-w-10 dr-h-10 dr-text-blue-500" />
                  </div>
                ) : (
                  <div className="dr-w-18 dr-h-18 dr-rounded-full dr-bg-gray-50 dr-flex dr-items-center dr-justify-center">
                    <IconComponent className="dr-w-10 dr-h-10 dr-text-gray-400" />
                  </div>
                )}
              </div>

              <div className="dr-flex-1 dr-min-w-0">
                <p
                  className={`dr-text-14 dr-font-500 dr-mb-1 ${status === "completed"
                      ? "dr-text-darwin-green-primary"
                      : status === "active"
                        ? "dr-text-blue-600"
                        : "dr-text-gray-500"
                    }`}
                >
                  {step.title}

                  {status === "active" && (
                    <span className="dr-ml-2 dr-text-12 dr-font-400 dr-text-blue-500">
                      In Progress...
                    </span>
                  )}
                </p>
                <p className="dr-text-12 dr-text-darwin-neutral-600 dr-leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dr-mt-16">
        <div className="dr-w-full dr-bg-gray-200 dr-rounded-full dr-h-2">
          <div
            className="dr-bg-blue-500 dr-h-2 dr-rounded-full dr-transition-all dr-duration-500"
            style={{
              width: `${((progressSteps.findIndex((s) => getStepStatus(s.key) === "active") + 1) / progressSteps.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ReceiptActionFooter() {
  const closeReceiptPopup = useReceiptStore(
    (state) => state.setCloseReceiptPopup
  );
  const selectedAppointment = useReceiptStore(
    (state) => state.selectedAppointment
  );

  const receipt = useReceiptStore((state) => state.receipt);
  const receiptId = useReceiptStore((state) => state.receiptId);
  return (
    <div className="dr-flex dr-justify-between dr-items-center dr-p-16 dr-bg-gray-50">
      <div className="dr-flex dr-space-x-12">
        {receipt?.receipt_type === RECEIPT_TYPE.DUE_PAYMENT ||
          !!receipt?.payment_id ? null : (
          <SecondaryButton
            title="Edit"
            onClick={() => {
              if (receiptId) {
                editReceipt({
                  appointment: selectedAppointment,
                  receiptId: receiptId,
                });
              }
            }}
            extraClass={getAclViewClassName(SUBJECT_KEYS.RECEIPT_EDIT)}
          />
        )}
        <SecondaryButton
          title="New Receipt"
          onClick={() => {
            if (selectedAppointment) {
              createNewReceipt({ appointment: selectedAppointment });
            }
          }}
        />
        <SecondaryButton
          title="Refund"
          onClick={() => {
            if (selectedAppointment) {
              createNewReceipt({ appointment: selectedAppointment });
            }
          }}
        />
      </div>
      <div>
        <PrimaryButton
          title="Done"
          onClick={() => {
            closeReceiptPopup();
            closeReceiptPopupEvent();
          }}
        />
      </div>
    </div>
  );
}
